'use client';

/**
 * THE APP BAR: the one 44 px glass header wherever an app is shown on its
 * own (wireframes v3, sections A, B and E; the owner's notes of 2026-09-29).
 * The phone app view mounts it (lane C, node-frame.tsx); desktop full screen
 * and the app's own address (`/w/` on revent.robutler.app) mount it too
 * (lane B). One component so the three read as one thing.
 *
 * WHAT IT IS, left to right: the app's icon and name, the ad slot, the ⋯
 * button. Nothing else. It is deliberately NOT a window title bar: no
 * traffic lights, no border or shadow of its own, no drag handle, no
 * double-tap, no long press, no resize edges. It sits IN FLOW above the app
 * (never over it): the app's box starts under the bar's hairline.
 *
 * GLASS. `.ws-app-bar` in app/globals.css: the surface token at 72%, a 20 px
 * backdrop blur with a saturation boost, and a 1 px hairline under it, in
 * both themes. That is the house `.liquid-glass` recipe without its drop
 * shadow and bevel, a little more opaque and blurred more, so it never reads
 * as a floating pill.
 *
 * THE TITLE FADE (the owner, 2026-09-29: "fade the long app titles under the
 * ad slot"). The ad slot keeps its 160 px minimum while an ad may still
 * arrive or is drawn (`reserveMinWidth` on AdSlot). The name has `flex: 0 1
 * auto` and `min-width: 0`, so it is the one thing that gives way, and when
 * it overflows it is clipped at the slot's start with a 24 px fade
 * (`mask-image`, `.ws-app-bar-name[data-overflow]`), never an ellipsis. With
 * no ad, or once the slot resolved to nothing, the reservation lets go, the
 * name takes the full width and fades only if it still overflows before ⋯.
 * The icon never shrinks.
 *
 * THE SWIPE (handoff section 4.3; phone variant only, when `onSwipe` is
 * given). A sideways swipe on the bar goes to the previous or next app; it
 * replaces the canvas's 28 px edge strips, which stole taps from an app's
 * edge controls and competed with Android's Back gesture. A swipe that ends
 * on the ad link or on ⋯ must not click them, so the bar swallows the click
 * that follows a swipe (`onClickCapture`).
 *
 * THE DESKTOP SLOT STOPS AT 470 PX (lane B; wireframes v3, section B: "on a
 * wide screen it stops growing at the title bar's 470 px and centres in the
 * free middle"). A desktop bar is as wide as the screen in full screen or on
 * the app's own address, so its free middle can be a thousand pixels; the
 * slot region sits inside a `flex-1` wrapper that centres it and is capped at
 * `APP_BAR_AD_MAX_WIDTH_PX`, so the ad lands in the middle of the bar, never
 * hard against the name. The phone bar keeps its slot as a direct child: the
 * 160 px reservation and the title fade depend on it.
 *
 * PROPS
 *   variant     'phone' (⋯ opens the bottom sheet, the ad starts after the
 *               name, the bar takes the sideways swipe) or 'desktop' (⋯ is a
 *               dropdown, the ad centres in the free middle).
 *   icon        the app's icon, 24 px, already rendered (identityVisual in
 *               rail-dock.tsx is the house chain); null or absent draws none.
 *   name        the app's name as rendered; `nameText` is its plain text for
 *               the sheet's title and the accessible names (defaults to
 *               `name` when that is a string).
 *   placement   the ad placement this bar is ('appBar', 'fullScreen',
 *               'appAddress'); `appId` the app being shown (null: no ad);
 *               `adActive` whether this bar's slot is the device's one live
 *               slot (default true).
 *   menu        the ⋯ rows, in order: `{ id, label, icon?, onSelect,
 *               disabled?, active? }` or `{ kind: 'switch', id, label, icon?,
 *               checked, onChange }`. See app-bar-menu.tsx.
 *   onSwipe     phone only: called with -1 (previous) or 1 (next) app.
 *   onExit      when given, a one-click "Exit full screen" button sits
 *               right before ⋯, the same 44 px box and icon weight as ⋯
 *               (the desktop full-screen bar, NodeFrame; the owner,
 *               2026-09-30: full screen is the page's own view, so the way
 *               out has to be on the page, not the browser's Esc).
 *   action      the same one-click tile for a bar that has nothing to exit
 *               to (2026-10-02, the owner: "/w fullscreen ui should be the
 *               same as the ui we see after clicking the green fullscreen
 *               button"). The standalone `/w/` page puts its primary action
 *               there: back to the launching workspace for a `?ws=` view,
 *               else "Add to workspace". `{ label, icon, href | onSelect,
 *               busy?, testId? }`; a link is a real `<a>`. One tile per bar:
 *               `onExit` wins when both are given, and both are drawn by
 *               the same code, so the box, the tile and the icon weight
 *               cannot drift between the two bars.
 *   unread      the person's unread notifications, drawn as a count chip on
 *               ⋯ when above zero (2026-10-03). Given only by a bar that
 *               stands where the usual badge is not on the page: full
 *               screen hides the rail's avatar on a desktop and the bottom
 *               bar's on a phone, and the menu's first row is then
 *               Notifications, where the chip leads (node-frame.tsx). The
 *               chip is the bottom bar's own, one size up on a desktop.
 *               Absent, null or 0: no chip.
 *   className, testId
 */

import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Loader2, Minimize2, MoreHorizontal } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { AdSlot, AD_PICTURE_HEIGHT_APP_BAR, AD_SLOT_MIN_WIDTH_APP_BAR } from '@/components/ads/ad-slot';
import type { AdPlacement } from '@/lib/ads/types';
import { cn } from '@/lib/utils';
import { unreadBadgeText } from '@/components/workspace/unread-notifications';
import { AppBarMenu, type AppBarMenuRow } from './app-bar-menu';

export type {
  AppBarMenuRow,
  AppBarMenuAction,
  AppBarMenuSwitch,
  AppBarMenuLink,
  AppBarMenuHeading,
} from './app-bar-menu';

/** The bar's height, hairline included. The app's box starts here. */
export const APP_BAR_HEIGHT_PX = 44;
/** The title's fade, in px, where it is clipped at the slot's start. */
export const APP_BAR_TITLE_FADE_PX = 24;
/** A sideways swipe on the bar must travel at least this far to change app. */
export const APP_BAR_SWIPE_PX = 60;
/**
 * The ad's clearance inside the slot, phone: none at the start (the bar's own
 * gap gives 16 px after the name) and 16 px before ⋯. On a desktop the slot
 * keeps the SAME clearance on both sides, so the ad sits in the centre of the
 * free middle (the design pass of 2026-09-30 measured it 16 px left of centre
 * under the old 16 / 48 pair). Applied only while an ad is drawn.
 */
export const APP_BAR_AD_CLEARANCE = {
  phone: { start: 0, end: 16 },
  desktop: { start: 32, end: 32 },
} as const;
/**
 * The visible part of a bar button: a 32 px tile inside the 44 px hit box,
 * which shows hover, press and keyboard focus (the design pass of
 * 2026-09-30: the buttons only changed their icon's opacity). `relative`
 * because ⋯ anchors its unread chip to the tile's corner; it is here, on
 * the one constant, so every tile still wears the same classes.
 *
 * THE GROUP HAS A NAME (2026-10-03). The tile lights for ITS button, and a
 * bare `group-hover:` answers to any ancestor marked `group`: the window
 * frame is one (node-frame.tsx, for its own title bar), so in full screen,
 * where the window is the whole page, both tiles sat lit whenever the
 * pointer was anywhere on the page, hovered or not (the visual pass
 * measured the Exit tile at 10% ink with `:hover` false). `group/tile` on
 * the button and `/tile` on these three states tie them to each other.
 */
const BAR_BUTTON_TILE =
  'relative flex h-8 w-8 items-center justify-center rounded-md transition-colors group-hover/tile:bg-foreground/10 group-active/tile:bg-foreground/15 group-focus-visible/tile:ring-1 group-focus-visible/tile:ring-ring';
/**
 * The 44 px box of the one-click tile before ⋯ (Exit full screen, or the
 * standalone page's action): the same box as ⋯, pulled 8 px towards it.
 */
const BAR_TILE_BOX =
  'group/tile -mr-2 flex h-11 w-11 shrink-0 items-center justify-center text-foreground/80 transition-colors hover:text-foreground focus-visible:outline-none';
/** The widest a desktop bar's ad slot grows (the title bar's number), centred in the free middle. */
export const APP_BAR_AD_MAX_WIDTH_PX = 470;
/** The ad's small picture in this 44 px bar (the picture round, 2026-09-30); the 40 px title bar uses the slot's default, 24. */
export const APP_BAR_AD_PICTURE_PX = AD_PICTURE_HEIGHT_APP_BAR;

/**
 * The one-click tile of a bar with nothing to exit to (see the file note,
 * `action`). A `href` draws a real link, so a middle click and the status
 * bar work; otherwise `onSelect` runs on a click. `busy` spins the icon and
 * ignores clicks while the action's request is in flight.
 */
export interface AppBarAction {
  /** Plain words: the accessible name and the tooltip (the tile shows only the icon). */
  label: string;
  icon: ComponentType<{ className?: string }>;
  href?: string;
  onSelect?: () => void;
  busy?: boolean;
  testId?: string;
}

/** The resolved tile, whichever prop supplied it (see `tileSpec` in the component). */
interface TileSpec {
  label: string;
  title: string;
  Icon: ComponentType<{ className?: string }>;
  onSelect?: () => void;
  href?: string;
  busy: boolean;
  testId: string;
}

export interface AppBarProps {
  variant: 'phone' | 'desktop';
  icon?: ReactNode;
  name: ReactNode;
  nameText?: string;
  placement: AdPlacement;
  appId: string | null;
  adActive?: boolean;
  menu: AppBarMenuRow[];
  onSwipe?: (dir: 1 | -1) => void;
  onExit?: () => void;
  action?: AppBarAction | null;
  unread?: number | null;
  className?: string;
  testId?: string;
}

export function AppBar({
  variant,
  icon = null,
  name,
  nameText,
  placement,
  appId,
  adActive = true,
  menu,
  onSwipe,
  onExit,
  action = null,
  unread = null,
  className,
  testId = 'app-bar',
}: AppBarProps) {
  const t = useT();
  const phone = variant === 'phone';
  const unreadCount = typeof unread === 'number' && unread > 0 ? unread : 0;
  const label = nameText ?? (typeof name === 'string' || typeof name === 'number' ? String(name) : '');
  const nameRef = useRef<HTMLSpanElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // The fade only when the name really overflows its box: a name that fits
  // must not lose its last letters to the mask, and a `flex: 0 1 auto` box is
  // exactly as wide as its text whenever there is room.
  useEffect(() => {
    const el = nameRef.current;
    if (!el) return;
    const measure = () => setOverflow(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [name]);

  // The sideways swipe (phone). No pointer capture: a capture would retarget
  // the click that follows, and a tap on the ad link or ⋯ must still be
  // theirs. A swipe sets `swallowClick` so the click after it is dropped.
  const swipe = useRef<{ id: number; x: number; y: number; fired: boolean } | null>(null);
  const swallowClick = useRef(false);
  const swipeOn = phone && !!onSwipe;
  const onPointerDown = (e: React.PointerEvent) => {
    if (!swipeOn || e.button !== 0) return;
    swipe.current = { id: e.pointerId, x: e.clientX, y: e.clientY, fired: false };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = swipe.current;
    if (!s || s.fired || s.id !== e.pointerId) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dx) >= APP_BAR_SWIPE_PX && Math.abs(dx) > 2 * Math.abs(dy)) {
      s.fired = true;
      swallowClick.current = true;
      onSwipe?.(dx < 0 ? 1 : -1);
    }
  };
  const endSwipe = () => {
    swipe.current = null;
  };

  // One slot element, placed by the variant below. The desktop cap is the
  // literal Tailwind class for `APP_BAR_AD_MAX_WIDTH_PX` (a class the build
  // can see; the app-bar test ties the two together).
  const slotEl = (placeClass: string) => (
    <AdSlot
      placement={placement}
      appId={appId}
      active={adActive}
      minWidth={AD_SLOT_MIN_WIDTH_APP_BAR}
      clearance={APP_BAR_AD_CLEARANCE[variant]}
      align={phone ? 'start' : 'center'}
      reserveMinWidth={phone}
      // The 44 px bar draws the ad's small picture 28 px tall (the 40 px
      // title bar draws it 24). It sits after the Ad label, never beside
      // the app's own icon, which is at the far left before the name.
      pictureHeight={APP_BAR_AD_PICTURE_PX}
      className={phone ? placeClass : cn(placeClass, 'max-w-[470px]')}
    />
  );

  // THE ONE-CLICK TILE before ⋯: Exit full screen (`onExit`, the desktop
  // full-screen bar) or the standalone page's action (`action`), drawn by one
  // piece of code so the two bars cannot drift (2026-10-02). Exit keeps its
  // "(Esc)" hint: on the page, Esc is the other way out of full screen.
  const tileSpec: TileSpec | null = onExit
    ? {
        label: t('content.exitFullscreen'),
        title: `${t('content.exitFullscreen')} (Esc)`,
        Icon: Minimize2,
        onSelect: onExit,
        busy: false,
        testId: 'app-bar-exit',
      }
    : action
      ? {
          label: action.label,
          title: action.label,
          Icon: action.busy ? Loader2 : action.icon,
          onSelect: action.onSelect,
          href: action.href,
          busy: !!action.busy,
          testId: action.testId ?? 'app-bar-action',
        }
      : null;
  const TileIcon = tileSpec?.Icon ?? null;
  const tileIcon = tileSpec && TileIcon ? (
    <span className={BAR_BUTTON_TILE}>
      <TileIcon className={cn('h-5 w-5', tileSpec.busy && 'animate-spin')} />
    </span>
  ) : null;
  const tile = !tileSpec ? null : tileSpec.href && !tileSpec.busy ? (
    <a
      href={tileSpec.href}
      aria-label={tileSpec.label}
      title={tileSpec.title}
      data-testid={tileSpec.testId}
      onClick={(e) => {
        e.stopPropagation();
        tileSpec.onSelect?.();
      }}
      className={BAR_TILE_BOX}
    >
      {tileIcon}
    </a>
  ) : (
    <button
      type="button"
      aria-label={tileSpec.label}
      title={tileSpec.title}
      aria-busy={tileSpec.busy || undefined}
      data-testid={tileSpec.testId}
      onClick={(e) => {
        e.stopPropagation();
        if (tileSpec.busy) return;
        tileSpec.onSelect?.();
      }}
      className={cn(BAR_TILE_BOX, tileSpec.busy && 'pointer-events-none opacity-60')}
    >
      {tileIcon}
    </button>
  );

  return (
    <div
      data-testid={testId}
      data-app-bar-variant={variant}
      aria-label={label || undefined}
      className={cn(
        'ws-app-bar nodrag nopan relative z-10 flex shrink-0 select-none items-center gap-2 pl-3',
        className,
      )}
      style={{ height: APP_BAR_HEIGHT_PX, touchAction: swipeOn ? 'pan-y' : undefined }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endSwipe}
      onPointerCancel={endSwipe}
      onPointerLeave={endSwipe}
      onClickCapture={(e) => {
        if (!swallowClick.current) return;
        swallowClick.current = false;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {icon ? (
        <span aria-hidden className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden">
          {icon}
        </span>
      ) : null}
      <span
        ref={nameRef}
        data-testid="app-bar-name"
        data-overflow={overflow ? 'true' : undefined}
        className="ws-app-bar-name min-w-0 overflow-hidden whitespace-nowrap text-[15px] font-semibold leading-normal text-foreground"
        style={{ flex: '0 1 auto' }}
      >
        {name}
      </span>
      {/* The slot owns the rest of the row (`min-w-0 flex-1` inside). The
          8 px row gap plus this margin keep 16 px between the name and an ad;
          empty, the region is a zero-size spacer the name stretches past. On
          a desktop the region sits in a centring wrapper and stops at 470 px
          (see the file note); on a phone it is the row's own child. */}
      {phone ? (
        slotEl('ml-2 self-stretch')
      ) : (
        <div data-testid="app-bar-ad-middle" className="ml-2 flex min-w-0 flex-1 justify-center self-stretch">
          {slotEl('self-stretch')}
        </div>
      )}
      {tile}
      <div className="relative shrink-0">
        <button
          ref={moreRef}
          type="button"
          // With something unread the name says so: the chip is drawn, not
          // read, and "App menu" alone would hide what it is there for.
          aria-label={
            unreadCount > 0
              ? `${t('workspace.node.appMenu')}, ${t('workspace.node.unread', { count: unreadCount })}`
              : t('workspace.node.appMenu')
          }
          aria-haspopup={phone ? 'dialog' : 'menu'}
          aria-expanded={menuOpen}
          data-testid="app-bar-more"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          className={cn(
            'group/tile flex h-11 w-11 items-center justify-center text-foreground/80 transition-colors hover:text-foreground focus-visible:outline-none',
            menuOpen && 'text-foreground',
          )}
        >
          <span className={cn(BAR_BUTTON_TILE, menuOpen && 'bg-foreground/10')}>
            <MoreHorizontal className="h-5 w-5" />
            {/* The unread chip (see `unread` in the file note), at the
                tile's corner: the bottom bar's own chip (the destructive
                token, ringed in the page colour), one size up on a desktop,
                where the rail avatar's chip it stands in for is 16 px. The
                cap is 9 on both: the tile is 32 px. Not the window buttons'
                red: this bar is not a title bar and wears none of their
                colours. The 16 px one sits 4 px above the tile, not 2: at 2
                its ring ended at the tile's middle and shaved the top off
                two of the three dots (the visual pass of 2026-10-03). */}
            {unreadCount > 0 && (
              <span
                aria-hidden
                data-testid="app-bar-unread"
                className={cn(
                  'absolute grid place-items-center rounded-full bg-destructive px-1 font-semibold leading-none text-destructive-foreground ring-2 ring-background',
                  phone ? '-right-1 -top-0.5 h-3.5 min-w-3.5 text-[9px]' : '-right-0.5 -top-1 h-4 min-w-4 text-[10px]',
                )}
              >
                {unreadBadgeText(unreadCount, 9)}
              </span>
            )}
          </span>
        </button>
        <AppBarMenu variant={variant} open={menuOpen} title={label} rows={menu} onClose={closeMenu} returnFocusTo={moreRef} />
      </div>
    </div>
  );
}
